import { reactive } from "vue";

import type {
  CustomFormAnswer,
  CustomFormProgress,
  CustomFormSubmissionStatus
} from "@/typings/customForm";

// Save-as-you-go for one submission's answers.
//
// Five things make this more than a debounced PATCH:
//
//   * lock_version. The server rejects a write to an existing answer that omits
//     it, and returns 409 for a stale one. So the version has to be carried from
//     the last server response, not from whatever the row was rendered with.
//   * Identity is resolved when the write is sent, not when the edit was made.
//     The first write to an unanswered question is a POST that mints the answer;
//     an edit queued before it returned was composed when there was no id yet.
//     Reading identity at send time means the follow-up PATCHes the new row
//     instead of POSTing a second time.
//   * Writes to one question are serialized. Without that, the read above still
//     races: two POSTs would go out together and the server would reject the
//     second for a missing lock_version, losing the newer edit. Serializing also
//     keeps responses in order, so a slow save cannot land after a newer one and
//     reinstate an older value — which is why no request-token guard is needed.
//   * The server is the authority on the saved answer. WriteAnswer re-snapshots
//     field_label/field_type and can clear a selection a template edit stranded
//     (drop_stranded_tokens), so the response is adopted rather than assumed.
//   * A 409 must stop, not retry. Retrying would be the last-write-wins the
//     server went out of its way to prevent.
export type SaveState = "idle" | "saving" | "saved" | "conflict" | "error";

export interface AnswerWriteResult {
  answer: CustomFormAnswer;
  progress: CustomFormProgress;
  // A write can change it: amending a submitted form returns it to in_progress,
  // so the caller has to adopt this rather than assume the status it rendered.
  status: CustomFormSubmissionStatus;
}

const DEBOUNCE_MS = 600;

interface WriteTarget {
  answerId: number | null;
  attributes: Record<string, unknown>;
}

// What the caller knows about a question's answer right now. Null answerId means
// nothing has been written yet, so the write is an upsert keyed on the question.
export interface AnswerIdentity {
  answerId: number | null;
  lockVersion: number | null;
}

export interface AnswerSaveOptions {
  write: (payload: WriteTarget) => Promise<AnswerWriteResult>;
  // Called at send time, so it must read live state rather than a captured copy.
  identify: (questionId: number) => AnswerIdentity;
  onSaved: (result: AnswerWriteResult) => void;
  onConflict: (questionId: number) => void;
  debounceMs?: number;
}

export function useAnswerSave({
  write,
  identify,
  onSaved,
  onConflict,
  debounceMs = DEBOUNCE_MS
}: AnswerSaveOptions) {
  const states = reactive<Record<number, SaveState>>({});
  // True from the first edit until nothing is queued or in flight for that
  // question. Inputs are fully controlled from the answer the caller adopts, and
  // that lands the moment a response does — so a control that is still being typed
  // into needs to know a write of its own is outstanding and keep its own text.
  // states cannot answer this: it is "saved" between a response landing and the
  // follow-up edit's debounce firing, and never returns to "idle" at all.
  const outstanding = reactive<Record<number, boolean>>({});
  const timers = new Map<number, ReturnType<typeof setTimeout>>();
  // Held separately from the timer so flushPending can still send an edit whose
  // debounce has not fired — otherwise the last keystroke before navigating away
  // would be silently dropped.
  const pending = new Map<number, Record<string, unknown>>();
  // Per question, so the next write waits for the one before it.
  const inFlight = new Map<number, Promise<void>>();

  function stateFor(questionId: number): SaveState {
    return states[questionId] ?? "idle";
  }

  function isOutstanding(questionId: number): boolean {
    return outstanding[questionId] ?? false;
  }

  // Only once both the queue and the wire are empty for this question: a follow-up
  // edit composed while the previous write was in flight is still unsent work.
  function settle(questionId: number) {
    if (pending.has(questionId) || inFlight.has(questionId)) return;

    outstanding[questionId] = false;
  }

  function clearTimer(questionId: number) {
    const timer = timers.get(questionId);
    if (timer) {
      clearTimeout(timer);
      timers.delete(questionId);
    }
  }

  // The question id doubles as the upsert key: a write with no answer yet carries
  // it so the server can find-or-initialize, and an existing one carries the
  // version instead so the stale check runs.
  function target(
    questionId: number,
    attributes: Record<string, unknown>
  ): WriteTarget {
    const { answerId, lockVersion } = identify(questionId);
    if (answerId === null) {
      return {
        answerId: null,
        attributes: { ...attributes, custom_form_question_id: questionId }
      };
    }

    return {
      answerId,
      attributes: { ...attributes, lock_version: lockVersion }
    };
  }

  async function flush(
    questionId: number,
    attributes: Record<string, unknown>
  ) {
    states[questionId] = "saving";

    try {
      const result = await write(target(questionId, attributes));
      states[questionId] = "saved";
      onSaved(result);
    } catch (error) {
      // 409 is the stale-edit and concurrent-create signal. It is not retryable:
      // the caller re-reads the answer so the user sees what actually landed.
      if (isConflict(error)) {
        states[questionId] = "conflict";
        onConflict(questionId);
        return;
      }
      // Anything else the global interceptor has already flashed.
      states[questionId] = "error";
    }
  }

  function run(questionId: number): Promise<void> {
    clearTimer(questionId);
    const attributes = pending.get(questionId);
    if (!attributes) return Promise.resolve();

    pending.delete(questionId);
    // Queued behind the previous write for this question so identity is resolved
    // against its result. flush never rejects, so the catch is belt-and-braces.
    const chained = (inFlight.get(questionId) ?? Promise.resolve())
      .catch(() => {})
      .then(() => flush(questionId, attributes));

    inFlight.set(questionId, chained);
    return chained.finally(() => {
      if (inFlight.get(questionId) === chained) inFlight.delete(questionId);
      settle(questionId);
    });
  }

  // Debounced per question, so typing does not fire a write per keystroke while a
  // select or a checkbox still saves on its single change (immediate).
  function save(
    questionId: number,
    attributes: Record<string, unknown>,
    { immediate = false } = {}
  ) {
    clearTimer(questionId);
    pending.set(questionId, attributes);
    // Before the debounce, not after it: the gap between the keystroke and the
    // write going out is exactly when an earlier response can land on the field.
    outstanding[questionId] = true;

    if (immediate) return run(questionId);

    timers.set(
      questionId,
      setTimeout(() => run(questionId), debounceMs)
    );
    return Promise.resolve();
  }

  // For unmount and before navigating away: sends everything still debounced, and
  // waits on anything already in flight so a queued follow-up is not abandoned.
  function flushPending() {
    return Promise.all([...[...pending.keys()].map(run), ...inFlight.values()]);
  }

  function cancelAll() {
    [...timers.keys()].forEach(clearTimer);
    pending.clear();
    Object.keys(outstanding).forEach((id) => settle(Number(id)));
  }

  return { states, stateFor, isOutstanding, save, flushPending, cancelAll };
}

// The interceptor shapes errors inconsistently across callers, so both the axios
// response and a bare status are accepted rather than assuming one.
function isConflict(error: unknown): boolean {
  const status =
    (error as { response?: { status?: number } })?.response?.status ??
    (error as { status?: number })?.status;
  return status === 409;
}
